import { GetInvoiceDetailsChargesQuery } from '../queries/getInvoiceDetailsCharges.query.generated';
import { AMOUNT_DUE_UNAVAILABLE_TEXT, type InvoiceAmountDue } from '../hooks/useInvoiceAmountDue';
import {
  getCurrentArgroups,
  groupChargesByMove,
  invoiceVehicleMoves,
  isSettledPaymentStatus,
  type ChargesByMoveGroup as SdkChargesByMoveGroup,
} from '@hopdrive/sdk-ar';

/**
 * Invoice lines come from sdk-ar's shared grouping, so dealer, admin and the
 * emailed PDF print the same rows.
 */
export { formatAddressShort, getOtherCharges, getProductCharges, groupChargesByMove, invoiceVehicleMoves } from '@hopdrive/sdk-ar';

/** A move or guest ride row. This portal's queries return scenario ids as numbers. */
export type ChargesByMoveGroup = Omit<SdkChargesByMoveGroup, 'scenarioId'> & { scenarioId?: number | null };

/**
 * Calculates total mileage from charges (via their source moves)
 */
export const getTotalMileageFromCharges = (argroups: any[] = []): number =>
  // Every vehicle leg across the rows: a scenario row can hold a concierge and its loaner (#3195).
  invoiceVehicleMoves(groupChargesByMove(argroups)).reduce(
    (total: number, move: any) => total + Number(move?.lane?.distance_miles || 0),
    0,
  );

/** What the totals print in place of the Amount Due while sdk-ar's answer loads. */
export const AMOUNT_DUE_LOADING_TEXT = '…';

/**
 * The billing groups whose charges the invoice's Amount Due is asked about:
 * every charge the totals count, moves and products, from the details query's
 * aggregates. Those leave out card processing fees and read done groups only.
 */
type AmountDueChargeGroups = ReadonlyArray<{ archarges: ReadonlyArray<{ id: number }> }>;
export const amountDueArgroups = (
  invoice?: { move_charges_aggregate?: AmountDueChargeGroups | null; product_charges_aggregate?: AmountDueChargeGroups | null } | null
): AmountDueChargeGroups => [
  ...(invoice?.move_charges_aggregate ?? []),
  ...(invoice?.product_charges_aggregate ?? []),
];

/**
 * Builds amount object from argroups/archarges (replaces buildAmountObject for charge-based invoices).
 * `amountDue` is sdk-ar's answer for the invoice, printed as given.
 */
export const buildAmountObjectFromCharges = (
  invoice: GetInvoiceDetailsChargesQuery['arinvoices_by_pk'],
  amountDue: number
) => {
  const amount: any = {};

  if (!invoice) return amount;

  const argroups = invoice.argroups || [];
  const currentGroups = getCurrentArgroups(argroups);
  const arpayments = invoice.arpayments || [];

  // Get all charges and credits from current groups only
  let allCharges: any[] = [];
  let allCredits: any[] = [];

  currentGroups.forEach(group => {
    allCharges = allCharges.concat(group.archarges || []);
    allCredits = allCredits.concat(group.arcredits || []);
  });

  // Separate move-related charges from products. Processing-fee charges (source_type 'arpayment',
  // type 'processing_fee') are excluded from this query's argroups, but guard against them here so
  // a fee can never be miscounted into moveDueTotal.
  // Products are known by their stored source_type. No product charge carries type 'product'.
  const moveCharges = allCharges.filter(c => c.source_type !== 'product' && c.type !== 'processing_fee');
  const productCharges = allCharges.filter(c => c.source_type === 'product');

  // Calculate charge totals
  const chargesSubtotal = allCharges.reduce((sum, charge) => sum + Number(charge.amount || 0), 0);
  const movesSubtotal = moveCharges.reduce((sum, charge) => sum + Number(charge.amount || 0), 0);
  const productSubtotal = productCharges.reduce((sum, charge) => sum + Number(charge.amount || 0), 0);

  // Calculate credit totals
  const creditsTotal = allCredits.reduce((sum, credit) => sum + Number(credit.amount || 0), 0);

  // Calculate payment totals
  const paidPayments = arpayments.filter(p => isSettledPaymentStatus(p.status));
  const paymentAmount = paidPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);

  // Calculate paid amount from arpaymentdetails
  let paidAmount = 0;
  allCharges.forEach(charge => {
    if (charge.arpaymentdetails) {
      charge.arpaymentdetails.forEach((detail: any) => {
        if (isSettledPaymentStatus(detail.parent?.status)) {
          paidAmount += Number(detail.amount || 0);
        }
      });
    }
  });

  // Build amount object
  amount.subtotal = Number(chargesSubtotal.toFixed(2));
  amount.moveDueTotal = Number(movesSubtotal.toFixed(2));
  amount.productTotal = Number(productSubtotal.toFixed(2));
  amount.discounted = Number(creditsTotal.toFixed(2)); // Credits replace discounts
  amount.credits = Number(creditsTotal.toFixed(2)); // drives the explicit Credits line (screen + PDF)
  amount.disputed = 0; // TODO: Implement dispute tracking for charges
  amount.paid = Number(paidAmount.toFixed(2));
  amount.paymentAmount = Number(paymentAmount.toFixed(2));
  amount.total = Number((chargesSubtotal - creditsTotal).toFixed(2));
  amount.due = amountDue;
  amount.payable = amountDue > 0 && invoice.status === 'closed';

  // Processing fees are their own charges (source_type 'arpayment') that this query excludes from
  // argroups, so read them from the paid payments' details instead of allCharges. The old
  // `type === 'fee'` filter matched nothing — the real type is 'processing_fee' — so this line
  // reported $0 on the statement even when a card fee had been charged.
  let processingFee = 0;
  arpayments.forEach((payment: any) => {
    if (isSettledPaymentStatus(payment?.status)) {
      (payment.details || []).forEach((detail: any) => {
        processingFee += Number(detail.archarge?.amount || 0);
      });
    }
  });
  amount.processingFee = Number(processingFee.toFixed(2));

  return amount;
};

/**
 * Builds the amount object from the aggregate fields on the invoice-details charges query
 * (move_charges_aggregate / product_charges_aggregate), as consumed by the invoice details
 * view. Kept as a pure function so the payable/due logic is testable.
 *
 * `due` is sdk-ar's answer (`useInvoiceAmountDue`). While it loads or when it cannot be read,
 * `due` is null and `dueText` says why, so no figure of this screen's own is printed. `payable`
 * requires a positive amount due on a closed invoice.
 */
export const buildAmountObjectFromAggregates = (invoice: any, amountDue: InvoiceAmountDue) => {
  let totalMoveCharges = 0;
  let totalProductCharges = 0;
  let totalCredits = 0;
  let totalMovePaid = 0;
  let totalProductPaid = 0;

  // Processing fees are their own charges attached to paid payment details.
  let totalProcessingFees = 0;
  invoice?.arpayments?.forEach((payment: any) => {
    if (isSettledPaymentStatus(payment?.status)) {
      payment?.details?.forEach((detail: any) => {
        totalProcessingFees += Number(detail.archarge?.amount || 0);
      });
    }
  });

  invoice?.move_charges_aggregate?.forEach((group: any) => {
    group?.archarges?.forEach((charge: any) => {
      totalMoveCharges += Number(charge.amount || 0);
      totalMovePaid += Number(charge.arpaymentdetails_aggregate?.aggregate?.sum?.amount || 0);
    });
    totalCredits += Number(group.arcredits_aggregate?.aggregate?.sum?.amount || 0);
  });

  invoice?.product_charges_aggregate?.forEach((group: any) => {
    group?.archarges?.forEach((charge: any) => {
      totalProductCharges += Number(charge.amount || 0);
      totalProductPaid += Number(charge.arpaymentdetails_aggregate?.aggregate?.sum?.amount || 0);
    });
  });

  const totalCharges = totalMoveCharges + totalProductCharges;
  const totalPaid = totalMovePaid + totalProductPaid;

  const payments = invoice?.arpayments || [];
  const paidPayments = payments.filter((p: any) => isSettledPaymentStatus(p.status));
  const paymentAmount = paidPayments.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);

  const total = totalCharges - totalCredits;
  const due = amountDue.status === 'ready' ? amountDue.amount : null;
  const dueText =
    amountDue.status === 'loading'
      ? AMOUNT_DUE_LOADING_TEXT
      : amountDue.status === 'unavailable'
        ? AMOUNT_DUE_UNAVAILABLE_TEXT
        : undefined;

  // Round at assignment (like the admin buildAmountObjectFromCharges port) so many-line
  // invoices don't surface float drift such as 50.000000000000007 in the displayed totals.
  return {
    subtotal: Number(totalCharges.toFixed(2)),
    moveDueTotal: Number(totalMoveCharges.toFixed(2)),
    productTotal: Number(totalProductCharges.toFixed(2)),
    discounted: Number(totalCredits.toFixed(2)),
    credits: Number(totalCredits.toFixed(2)), // drives the explicit Credits line (AR V2), mirrors buildAmountObjectFromCharges
    disputed: 0,
    paid: Number(totalPaid.toFixed(2)),
    paymentAmount: Number(paymentAmount.toFixed(2)),
    total: Number(total.toFixed(2)),
    due,
    dueText,
    payable: due != null && due > 0 && invoice?.status === 'closed',
    processingFee: Number(totalProcessingFees.toFixed(2)),
  };
};
